import { useEffect, useRef } from 'react';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { WS_EVENTS } from '@twinden/shared';
import { api } from '../lib/api';
import { forgetScopeKeys } from '../lib/e2ee/session';
import { forgetFamilyIndex } from '../lib/searchIndex';
import { discardConversations } from '../lib/outbox';
import {
  addDeletedNote,
  forgetKnownFamily,
  loadDeletedNotes,
} from '../lib/familyDeletedNote';
import {
  conversationsAtom,
  conversationsByFamilyAtom,
  currentFamilyAtom,
  currentFamilyIdAtom,
  currentUserAtom,
  documentsByFamilyAtom,
  familiesAtom,
  linksByFamilyAtom,
  listItemsByListAtom,
  listsByFamilyAtom,
  messagesAtom,
  openConversationIdAtom,
  removeFamilyAtom,
  setCurrentFamilyAtom,
  setFamiliesAtom,
  starredByFamilyAtom,
} from '../store';
import { t } from '../i18n';
import { useWebSocket } from './useWebSocket';

export interface FamilyDeletedPayload {
  familyId?: string;
  familyName?: string;
  deletedBy?: { id?: string; displayName?: string };
  conversationIds?: string[];
}

type Store = ReturnType<typeof getDefaultStore>;

/** Families whose deletion this tab asked for: no note for the deleter. */
const deletedHere = new Set<string>();
export function markDeletingHere(familyId: string) {
  deletedHere.add(familyId);
}
/** The delete was refused: a later event isn't this tab's doing. */
export function unmarkDeletingHere(familyId: string) {
  deletedHere.delete(familyId);
}

/** What members are told (D189), or, found out later, that it's gone. */
export function familyGoneText(familyName: string, deletedByName?: string) {
  return deletedByName
    ? t('account.deleteFamily.deletedBy', {
        family: familyName,
        name: deletedByName,
      })
    : t('account.deleteFamily.gone', { family: familyName });
}

function dropKey<T>(record: Record<string, T>, key: string) {
  if (!(key in record)) return record;
  const next = { ...record };
  delete next[key];
  return next;
}

/**
 * A family was deleted (M54, D189, D191): it goes from this device — its
 * chats, cached apps, keys, search index and unsent messages — and the app
 * moves to another family. With none left, and unless this person deleted
 * it, the waiting screen says who did. Returns what happened.
 */
export async function applyFamilyDeleted(
  payload: FamilyDeletedPayload,
  store: Store,
  notify: (title: string) => void
): Promise<{ conversationIds: string[]; noneLeft: boolean } | null> {
  const { familyId } = payload;
  if (!familyId) return null;
  const me = store.get(currentUserAtom)?.id ?? null;
  const familyName =
    payload.familyName ??
    store.get(familiesAtom).find((family) => family.id === familyId)?.name ??
    '';
  const byMe =
    deletedHere.has(familyId) ||
    (!!me && !!payload.deletedBy?.id && payload.deletedBy.id === me);

  const conversationIds = [
    ...new Set([
      ...(payload.conversationIds ?? []),
      ...store
        .get(conversationsAtom)
        .filter((chat) => chat.familyId === familyId)
        .map((chat) => chat.id),
    ]),
  ];

  // Nothing of it can be opened here any more (D191).
  forgetScopeKeys('family', familyId);
  for (const id of conversationIds) forgetScopeKeys('chat', id);
  void forgetFamilyIndex(familyId, conversationIds);
  discardConversations(conversationIds);

  const gone = new Set(conversationIds);
  store.set(
    conversationsAtom,
    store.get(conversationsAtom).filter((chat) => !gone.has(chat.id))
  );
  store.set(
    conversationsByFamilyAtom,
    dropKey(store.get(conversationsByFamilyAtom), familyId)
  );
  let messages = store.get(messagesAtom);
  for (const id of conversationIds) messages = dropKey(messages, id);
  store.set(messagesAtom, messages);
  if (gone.has(store.get(openConversationIdAtom) ?? '')) {
    store.set(openConversationIdAtom, null);
  }

  const lists = store.get(listsByFamilyAtom)[familyId] ?? [];
  let items = store.get(listItemsByListAtom);
  for (const list of lists) items = dropKey(items, list.id);
  store.set(listItemsByListAtom, items);
  store.set(listsByFamilyAtom, dropKey(store.get(listsByFamilyAtom), familyId));
  store.set(linksByFamilyAtom, dropKey(store.get(linksByFamilyAtom), familyId));
  store.set(
    documentsByFamilyAtom,
    dropKey(store.get(documentsByFamilyAtom), familyId)
  );
  store.set(
    starredByFamilyAtom,
    dropKey(store.get(starredByFamilyAtom), familyId)
  );

  const wasCurrent = store.get(currentFamilyIdAtom) === familyId;
  store.set(removeFamilyAtom, familyId);
  if (me) forgetKnownFamily(me, familyId);
  let remaining = store.get(familiesAtom);
  try {
    remaining = (await api.getFamilies()).data.filter(
      (family) => family.id !== familyId
    );
    store.set(setFamiliesAtom, remaining);
  } catch {
    // Offline: what's left locally will do until the next load.
  }
  const noneLeft = remaining.length === 0;
  if (wasCurrent && !noneLeft) {
    const next = remaining[0]!;
    store.set(currentFamilyIdAtom, next.id);
    try {
      store.set(setCurrentFamilyAtom, (await api.getFamily(next.id)).data);
    } catch {
      store.set(currentFamilyAtom, null);
    }
  }

  if (!byMe) {
    const deletedByName = payload.deletedBy?.displayName ?? '';
    // The last family: the waiting screen's note says it, and stays.
    // Otherwise a toast over the family the app moved to.
    if (noneLeft && me) {
      addDeletedNote(me, { familyId, familyName, deletedByName }, store);
    } else {
      notify(familyGoneText(familyName, deletedByName));
    }
  }
  deletedHere.delete(familyId);
  return { conversationIds, noneLeft };
}

/** Mounted once, in the app shell. */
export function useFamilyDeleted() {
  const { onEvent } = useWebSocket();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useAtomValue(currentUserAtom)?.id;
  // The latest location without re-subscribing on every navigation.
  const where = useRef(location);
  where.current = location;

  // A reload keeps the note until it's dismissed (D189).
  useEffect(() => {
    if (userId) loadDeletedNotes(userId, getDefaultStore());
  }, [userId]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.FAMILY_DELETED) return;
        const deleted = payload as FamilyDeletedPayload;
        void applyFamilyDeleted(deleted, getDefaultStore(), (title) =>
          toast({ title, status: 'info', duration: 8000 })
        ).then((result) => {
          if (!result) return;
          const { pathname, search } = where.current;
          const openChat = new URLSearchParams(search).get('conversation');
          // A page of that family (Manage family) or one of its chats.
          if (
            pathname.includes(deleted.familyId ?? '\0') ||
            (openChat && result.conversationIds.includes(openChat))
          ) {
            navigate('/', { replace: true });
          }
        });
      }),
    [onEvent, toast, navigate, userId]
  );
}
